// Globals
import { useTranslation } from 'react-i18next';

// Models
import { FeedbackStatus, FeedbackType, IFeedback } from '@/models/feedback.model';

// Store
import { useAppDispatch } from '@/store';
import { usePermissions, Resource, Permission } from '@/modules/Permissions';
import { changeFeedbackStatus, deleteFeedback, getFeedbacks } from '../../feature';

// Components
import { FeedbacksList } from '../../components';
import { StyledContentBlock } from '../../components/Styled';
import PartnersRequestsFiltersContainer from '../PartnersRequestsFiltersContainer/PartnersRequestsFiltersContainer';

// Helpers
import { getPartnersTableColumns } from '../../helpers/columns';
import { useGetFeedBacks } from '../../hooks';
import { GetFeedbacksDTO } from '../../dtos';
import { confirmAction } from '@/utils';

const PartnersRequestsContainer = () => {
  const { t } = useTranslation('feedbacks');
  const { list, isLoading, pagination, query } = useGetFeedBacks(FeedbackType.PARTNER_SUPPORT);
  const dispatch = useAppDispatch();
  const { canManage, can } = usePermissions();
  const canManageFeedback = canManage(Resource.FEEDBACK);
  const canDeleteFeedback = can(Permission.FEEDBACK_DELETE);

  const handleChangePage = (page: number) => {
    const dto = new GetFeedbacksDTO({ ...query, type: FeedbackType.PARTNER_SUPPORT, page });
    dispatch(getFeedbacks(dto));
  };

  const handleChangeStatus = async ({ id, status }: { id: string; status: FeedbackStatus }) => {
    await dispatch(
      changeFeedbackStatus({
        id,
        type: FeedbackType.PARTNER_SUPPORT,
        status,
      }),
    );
    dispatch(getFeedbacks({ ...query, type: FeedbackType.PARTNER_SUPPORT, page: 1 }));
  };

  const handleDelete = (feedback: IFeedback) => {
    const page =
      list?.length === 1 && (pagination?.current_page ?? 1) > 1
        ? pagination!.current_page - 1
        : pagination?.current_page ?? 1;

    confirmAction(t('feedback_delete_title'), async () => {
      await dispatch(deleteFeedback({ id: feedback.id, type: FeedbackType.PARTNER_SUPPORT })).unwrap();
      dispatch(getFeedbacks({ ...query, type: FeedbackType.PARTNER_SUPPORT, page }));
    });
  };

  return (
    <StyledContentBlock>
      <FeedbacksList
        isLoading={isLoading}
        currentPage={pagination?.current_page ?? 1}
        total={pagination?.total ?? 0}
        onPageChange={handleChangePage}
        list={list ?? []}
        columns={getPartnersTableColumns({
          t,
          canManageStatus: canManageFeedback,
          onChangeStatus: handleChangeStatus,
          ...(canDeleteFeedback ? { onDelete: handleDelete } : {}),
        })}
        components={{
          header: {
            wrapper: PartnersRequestsFiltersContainer,
          },
        }}
      />
    </StyledContentBlock>
  );
};

export default PartnersRequestsContainer;
